import { z } from 'zod';

/**
 * Canonical wire tag-id charset. Mirrors the Rust side: an identifier that may
 * carry path-like segments (e.g. `rs485/16/31/range_mm`). Must not start with a
 * separator.
 */
export const TAG_KEY_RE = /^[A-Za-z0-9_][A-Za-z0-9_./-]*$/;

export const TagTypeSchema = z.enum(['bool', 'int', 'float', 'string']);
export const TagScopeSchema = z.enum(['system', 'program', 'channel', 'sensor']);

export const TagSchema = z.object({
  id: z.string().min(1),
  project_id: z.string().min(1),
  name: z.string().min(1),
  tag_type: TagTypeSchema,
  scope: TagScopeSchema,
  description: z.string().nullable(),
  bound_channel_id: z.string().nullable(),
  units: z.string().nullable(),
  initial_value: z.string().nullable(),
  key: z.string().regex(TAG_KEY_RE).nullable().default(null),
  bound_sensor_id: z.string().nullable().default(null),
  sensor_field: z.string().nullable().default(null),
  archived_at_ms: z.number().int().nullable(),
  created_at_ms: z.number().int().nonnegative(),
  updated_at_ms: z.number().int().nonnegative(),
});

export const CreateTagSchema = z.object({
  name: z
    .string()
    .min(1, 'Name required')
    .regex(/^[A-Za-z_][A-Za-z0-9_.]*$/, 'Letters, digits, _ and . only; cannot start with a digit'),
  tag_type: TagTypeSchema,
  scope: TagScopeSchema,
  description: z.string().optional(),
  bound_channel_id: z.string().optional(),
  units: z.string().optional(),
  initial_value: z.string().optional(),
  key: z.string().regex(TAG_KEY_RE).optional(),
  bound_sensor_id: z.string().nullable().optional(),
  sensor_field: z.string().nullable().optional(),
});

export const UpdateTagSchema = z.object({
  name: z.string().min(1).optional(),
  description: z.string().optional(),
  units: z.string().optional(),
  initial_value: z.string().optional(),
  key: z.string().regex(TAG_KEY_RE).optional(),
});

export type ParsedTag = z.infer<typeof TagSchema>;
export type ParsedCreateTag = z.infer<typeof CreateTagSchema>;
export type ParsedUpdateTag = z.infer<typeof UpdateTagSchema>;
